/**
 * Deterministic filter hashing for Decision Engine feature matrix.
 *
 * Shared between backend (feature-matrix-relationships.ts) and frontend
 * (feature-matrices/utils.ts) to ensure filter IDs are always consistent.
 *
 * This module has NO backend-specific dependencies (no zapatos, no Source, etc.)
 * so it can be safely imported from frontend code via @hightouch/lib.
 */

/**
 * Produces a deterministic JSON string with sorted keys.
 * Ensures the same object produces the same string regardless of property order,
 * which can vary when objects are serialized/deserialized through JSONB storage.
 */
export function stableStringify(obj: unknown): string {
  if (obj === null || typeof obj !== "object") {
    return JSON.stringify(obj);
  }

  if (Array.isArray(obj)) {
    return "[" + obj.map(stableStringify).join(",") + "]";
  }

  const sortedKeys = Object.keys(obj).sort();
  const pairs = sortedKeys.map(
    (key) =>
      JSON.stringify(key) +
      ":" +
      stableStringify((obj as Record<string, unknown>)[key]),
  );
  return "{" + pairs.join(",") + "}";
}

/**
 * Generates a stable, short identifier from a filter condition.
 * Uses first 8 characters of SHA-256 hash for uniqueness (~4 billion possibilities).
 *
 * Uses Web Crypto API (crypto.subtle) which is available in both
 * browsers and Node.js 15+.
 */
export async function getFilterIdAsync(condition: object): Promise<string> {
  const json = stableStringify(condition);
  const data = new TextEncoder().encode(json);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hex.slice(0, 8);
}
